/**
 * ChromaticAberrationFilter - RGB channel shifting for glitch/vintage lens effect
 *
 * Simulates chromatic aberration (color fringing) by offsetting RGB channels:
 * - Red channel: shifted LEFT and UP
 * - Green channel: no shift (reference)
 * - Blue channel: shifted RIGHT and DOWN
 *
 * This creates the rainbow-edge effect seen in vintage lenses or intentional glitch art.
 *
 * Performance: ~90+ FPS on 1080p (minimal computation, coordinate mapping only)
 */

import { Filter, validateImageData } from "./Filter";

export class ChromaticAberrationFilter implements Filter {
  /**
   * Pixel offset for channel shifting (1-10)
   * Default: 3 provides visible aberration without excessive distortion
   * Higher values create more dramatic glitch effects but may degrade image quality
   */
  private offset = 3;

  /**
   * Temporary buffer for channel shifting
   * Reused across frames to avoid allocations in render loop
   * Reallocated only when image dimensions change
   */
  private tempBuffer: Uint8ClampedArray | null = null;

  /**
   * Apply chromatic aberration effect to image data
   *
   * @param imageData - Source image data to transform (mutated in-place)
   * @returns Modified ImageData with chromatic aberration applied
   */
  apply(imageData: ImageData): ImageData {
    validateImageData(imageData);
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;

    // Allocate temp buffer for channel shifting (reuse pattern)
    if (this.tempBuffer?.length !== data.length) {
      this.tempBuffer = new Uint8ClampedArray(data.length);
    }

    // Copy original data to temp buffer
    this.tempBuffer.set(data);

    // Iterate through all pixels (RGBA format: 4 bytes per pixel)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;

        // Red channel: shift LEFT and UP
        const redX = Math.max(0, x - this.offset);
        const redY = Math.max(0, y - this.offset);
        const redIndex = (redY * width + redX) * 4;
        data[i] = this.tempBuffer[redIndex] ?? 0;

        // Green channel: no shift (reference channel)
        data[i + 1] = this.tempBuffer[i + 1] ?? 0;

        // Blue channel: shift RIGHT and DOWN
        const blueX = Math.min(width - 1, x + this.offset);
        const blueY = Math.min(height - 1, y + this.offset);
        const blueIndex = (blueY * width + blueX) * 4;
        data[i + 2] = this.tempBuffer[blueIndex + 2] ?? 0;

        // Alpha: preserve from original (no shift)
        data[i + 3] = this.tempBuffer[i + 3] ?? 255;
      }
    }

    return imageData;
  }

  /**
   * Set filter parameters
   * @param params - Partial parameters to update
   */
  setParameters(params: Record<string, number>): void {
    if (params["offset"] !== undefined) {
      this.offset = Math.max(1, Math.min(10, Math.floor(params["offset"])));
    }
  }

  /**
   * Get default parameter values
   * @returns Default parameters object
   */
  getDefaultParameters(): Record<string, number> {
    return { offset: 3 };
  }

  /**
   * Release allocated buffer memory
   * Called when filter is no longer needed or app is shutting down
   */
  cleanup(): void {
    this.tempBuffer = null;
  }
}
